import { useLocation, useParams } from 'react-router-dom'
import { runsCopy, skillsCopy } from '@/copy'
import { Button } from '@/ui/Button/Button'
import { RunsExplorer } from '../components/RunsExplorer'
import { RunView } from '../components/runs/RunView'
import { WsPageShell } from '../components/WsPageShell'
import { ROUTES } from '../constants/routes'
import { useMeQuery } from '../hooks/queries'
import { SkillsPanel } from './workspace/SkillsPanel'

/**
 * Full-width workspace pages for the sidebar's WORKSPACE section. Skills and
 * Runs reuse the battle-tested panels that previously lived in workspace
 * settings, wrapped in the redesign's page shell.
 */

function useWorkspacePage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>()
  const { data: me } = useMeQuery()
  const ws = me?.workspaces.find((w) => w.workspace_id === workspaceId)
  const canManage = ws?.role === 'owner' || ws?.role === 'admin'
  return {
    workspaceId,
    workspaceName: ws?.name ?? skillsCopy.page.workspaceFallback,
    canManage,
  }
}

export function SkillsPage() {
  const { workspaceId, workspaceName, canManage } = useWorkspacePage()
  return (
    <WsPageShell
      crumbs={[
        { label: workspaceName, to: ROUTES.home },
        { label: skillsCopy.page.crumb },
      ]}
      title={skillsCopy.page.title}
      description={skillsCopy.page.description}
    >
      <SkillsPanel wsId={workspaceId} canManage={canManage} />
    </WsPageShell>
  )
}

export function RunsPage() {
  const { workspaceId, workspaceName } = useWorkspacePage()
  return (
    <WsPageShell
      crumbs={[
        { label: workspaceName, to: ROUTES.home },
        { label: runsCopy.page.crumb },
      ]}
      title={runsCopy.page.title}
      description={runsCopy.page.description}
      width="container-table"
    >
      <RunsExplorer
        workspaceId={workspaceId}
        caption={runsCopy.caption.workspace}
      />
    </WsPageShell>
  )
}

/**
 * One run's trace. `returnTo` rides in on navigation state so a run opened from
 * the admin Runs tab returns there rather than to this workspace's Runs page.
 */
export function RunDetailPage() {
  const { runId = '' } = useParams<{ runId: string }>()
  const { workspaceId, workspaceName } = useWorkspacePage()
  const { state } = useLocation() as { state: { returnTo?: string } | null }
  const runsUrl = state?.returnTo ?? ROUTES.workspaceRuns(workspaceId)

  return (
    <WsPageShell
      crumbs={[
        { label: workspaceName, to: ROUTES.home },
        { label: runsCopy.page.crumb, to: runsUrl },
        { label: runId.slice(0, 8) },
      ]}
      title={runsCopy.trace.title}
      description={runsCopy.trace.description}
      width="container-table"
      actions={
        <Button variant="ghost" href={runsUrl}>
          {runsCopy.trace.back}
        </Button>
      }
    >
      <RunView workspaceId={workspaceId} runId={runId} />
    </WsPageShell>
  )
}
